import { useCallback, useEffect, useState } from 'react';

import BottomSheet from '@/components/BottomSheet';
import { PrimaryButton } from '@/components/Buttons';
import {
  getPushPermissionStatus,
  registerForPush,
  type PushPermissionStatus,
} from '@/services/pushNotifications';
import { useTranslation } from '@/utils/i18n';
import { toast } from '@/stores/toast';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/hooks/queries/notifications';
import type { NotificationPreferences } from '@/api/notifications';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Per-notification opt-in/out (Profile → Notifications). One switch per
 * notification type; backend send paths each check their own flag.
 */
export default function NotificationSettingsSheet({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { data: preferences } = useNotificationPreferences();
  const mutation = useUpdateNotificationPreferences();
  // null = web (no OS permission concept) → just show the toggles.
  const [permission, setPermission] = useState<PushPermissionStatus | null>(null);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (open) void getPushPermissionStatus().then(setPermission);
  }, [open]);

  const activate = useCallback(async () => {
    setActivating(true);
    try {
      const ok = await registerForPush();
      setPermission(await getPushPermissionStatus());
      if (ok) toast.success(t('profile.notifications.registerSuccess'));
    } finally {
      setActivating(false);
    }
  }, [t]);

  const toggle = useCallback(
    (key: keyof NotificationPreferences, value: boolean) => {
      mutation.mutate(
        { [key]: value },
        { onError: () => toast.error(t('profile.notifications.updateError')) },
      );
    },
    [mutation, t],
  );

  const rows: { key: keyof NotificationPreferences; label: string; hint: string }[] = [
    {
      key: 'new_recommendations',
      label: t('profile.notifications.newReco'),
      hint: t('profile.notifications.newRecoHint'),
    },
    {
      key: 'daily_recommendations',
      label: t('profile.notifications.dailyDigest'),
      hint: t('profile.notifications.dailyDigestHint'),
    },
    {
      key: 'author_digest',
      label: t('profile.notifications.authorDigest'),
      hint: t('profile.notifications.authorDigestHint'),
    },
  ];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('profile.notifications.title')}
      testid="sheet-notification-settings"
    >
      {permission === 'prompt' && (
        <div
          className="tw:mb-3 tw:rounded-2xl tw:bg-dark-primary tw:p-4"
          data-testid="banner-push-activate"
        >
          <p className="tw:mb-3 tw:text-base tw:text-grey-3">{t('pushPrompt.body')}</p>
          <PrimaryButton
            label={t('profile.notifications.activate')}
            loading={activating}
            onClick={activate}
            data-testid="btn-push-activate"
          />
        </div>
      )}
      {permission === 'denied' && (
        <p
          className="tw:mb-3 tw:rounded-2xl tw:bg-dark-primary tw:p-4 tw:text-base tw:text-grey-3"
          data-testid="banner-push-denied"
        >
          {t('profile.notifications.permissionRequired')}
        </p>
      )}
      <div className="tw:flex tw:flex-col tw:gap-2">
        {rows.map(({ key, label, hint }) => {
          const checked = preferences?.[key] ?? true;
          return (
            <div
              key={key}
              className="tw:flex tw:min-h-16 tw:shrink-0 tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-3"
            >
              <div className="tw:flex tw:flex-1 tw:flex-col">
                <span className="tw:text-base tw:text-white">{label}</span>
                <span className="tw:legals tw:text-grey-4">{hint}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => toggle(key, !checked)}
                data-testid={`switch-${key}`}
                className={cn(
                  'tw:relative tw:h-7 tw:w-12 tw:shrink-0 tw:rounded-full tw:transition-colors',
                  checked ? 'tw:bg-yellow-primary' : 'tw:bg-grey-4/40',
                )}
              >
                <span
                  className={cn(
                    'tw:absolute tw:top-1 tw:size-5 tw:rounded-full tw:bg-white tw:transition-all',
                    checked ? 'tw:left-6' : 'tw:left-1',
                  )}
                />
              </button>
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}
